import { Dialog, Transition } from "@headlessui/react";
import { PhotographIcon, XIcon } from "@heroicons/react/solid";
import React, { Fragment, useEffect, useMemo, useState } from "react";
import { Link, matchPath, useHistory, useLocation } from "react-router-dom";
import ExternalLink from "../../../../components/ExternalLink";
import QrCode from "../../../../components/QrCode";
import { useAppearance } from "../../../../context/appearance";
import { useViewport } from "../../../../context/viewport";
import { useWallets } from "../../../../context/wallets";
import { breakpoints } from "../../../../utils/constants";
import WalletVerificationCountdown from "../WalletVerificationCountdown";

interface Props {}

const DonateStep: React.FC<Props> = () => {
  const history = useHistory();
  const location = useLocation();
  const { isDark } = useAppearance();
  const { wallets, getActiveWalletVerification } = useWallets();
  const { width } = useViewport();
  const isMobile = useMemo(() => width < breakpoints.sm, [width]);
  const [openGifModal, setOpenGifModal] = useState(false);

  const wizardPageMatch = matchPath<{
    walletAddress: string;
    stepKey: string;
  }>(location.pathname, {
    path: "/workspace/wallets/verify/:walletAddress/:stepKey"
  });

  const activeWalletVerification = useMemo(
    () =>
      getActiveWalletVerification(
        wizardPageMatch?.params.walletAddress as string
      ),
    [getActiveWalletVerification, wizardPageMatch?.params.walletAddress]
  );

  useEffect(() => {
    if (!activeWalletVerification) {
      history.push(
        `/workspace/wallets/verify/${wizardPageMatch?.params.walletAddress}/confirm-wallet`
      );
    }
  }, [
    activeWalletVerification,
    history,
    wallets,
    wizardPageMatch?.params.walletAddress
  ]);

  if (!activeWalletVerification) {
    return null;
  }

  return (
    <>
      <p className="mb-4">
        {isMobile ? (
          <>
            Please transfer {activeWalletVerification.verification_amount} HNT
            using the{" "}
            <ExternalLink
              className="inline-flex"
              text="Helium app"
              url="https://docs.helium.com/wallets/app-wallet"
            />{" "}
            from the wallet that controls the hotspots listed here to complete
            the wallet verification process. Complete this process on a desktop
            so that you can scan the QR code that will appear below.
          </>
        ) : (
          <>
            After scanning this QR code with the{" "}
            <ExternalLink
              className="inline-flex"
              text="Helium Wallet app"
              url="https://docs.helium.com/wallets/app-wallet"
            />
            , you will be prompted to transfer{" "}
            {activeWalletVerification.verification_amount} HNT to complete the
            wallet verification process.
          </>
        )}
      </p>

      <div className="flex items-start mb-4">
        <div style={{ height: "230px" }} className="hidden sm:block sm:mr-4">
          <QrCode
            size={230}
            background={isDark ? "#1f2937" : "#fff"}
            fill="#A96CEB"
            text={JSON.stringify({
              type: "payment",
              senderAddress: wizardPageMatch?.params.walletAddress as string,
              address: activeWalletVerification.donation_wallet,
              amount: activeWalletVerification.verification_amount.toString(),
              memo: "verify"
            })}
            radius={0.5}
          />
        </div>

        <div>
          <button
            type="button"
            className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-hotspotty-400 dark:bg-gray-900 dark:text-gray-400 dark:hover:text-gray-300 dark:hover:bg-gray-800 dark:border-gray-700 dark:hover:border-gray-400"
            onClick={() => {
              setOpenGifModal(true);
            }}
          >
            See how to use the app
            <PhotographIcon className="ml-2 inline-flex h-5 w-5" />
          </button>

          <div className="mt-6">
            <WalletVerificationCountdown
              walletAddress={wizardPageMatch?.params.walletAddress as string}
              bigSize
            />
          </div>
        </div>
      </div>

      <div className="flex-shrink-0 px-4 py-4 flex justify-end">
        <Link
          to={`/workspace/wallets/verify/${
            wizardPageMatch?.params.walletAddress as string
          }/summary`}
          className="ml-4 inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-hotspotty-400 hover:bg-hotspotty-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-hotspotty-400 flex items-center disabled:opacity-50 disabled:cursor-default"
        >
          Continue
        </Link>
      </div>

      <Transition.Root show={openGifModal} as={Fragment}>
        <Dialog
          as="div"
          static
          className="fixed z-50 inset-0 overflow-y-auto"
          open={openGifModal}
          onClose={setOpenGifModal}
        >
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <Dialog.Overlay className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
            </Transition.Child>

            {/* This element is to trick the browser into centering the modal contents. */}
            <span
              className="hidden sm:inline-block sm:align-middle sm:h-screen"
              aria-hidden="true"
            >
              &#8203;
            </span>
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-sm sm:w-full sm:p-6 dark:bg-gray-800">
                <div className="block absolute top-0 right-0 pt-4 pr-4">
                  <button
                    type="button"
                    className="bg-white rounded-md text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-hotspotty-400 dark:bg-transparent dark:text-gray-200 dark:hover:text-white"
                    onClick={() => setOpenGifModal(false)}
                  >
                    <span className="sr-only">Close</span>
                    <XIcon className="h-6 w-6" aria-hidden="true" />
                  </button>
                </div>

                <div className="mt-3 text-center sm:mt-5">
                  <div className="flex-shrink-0 flex flex-col items-center p-4">
                    <img
                      src="/wallet-verification-send-donation.gif"
                      alt="wallet verification send donation gif"
                    />
                  </div>
                </div>
              </div>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition.Root>
    </>
  );
};

export default DonateStep;
